import type { ReactNode } from "react";
import { Button } from "@electro-planner/ui";
import type { AutosaveStatus } from "../hooks/useAutosaveStatus.ts";

type Props = {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onExportPdf: () => void;
  onExportBom: () => void;
  onExportConductors: () => void;
  onOpenMeta: () => void;
  onOpenCommandPalette: () => void;
  onOpenReleaseNotes: () => void;
  autosave: AutosaveStatus;
  documentSwitcher?: ReactNode;
};

export function EditorToolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onExportPdf,
  onExportBom,
  onExportConductors,
  onOpenMeta,
  onOpenCommandPalette,
  onOpenReleaseNotes,
  autosave,
  documentSwitcher,
}: Props) {
  return (
    <div className="no-print flex items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5">
      {documentSwitcher ? (
        <>
          {documentSwitcher}
          <div className="mx-1 h-5 w-px bg-[var(--color-border)]" aria-hidden="true" />
        </>
      ) : null}
      <Button
        variant="ghost"
        size="sm"
        disabled={!canUndo}
        onClick={onUndo}
        title="Ångra (Ctrl+Z)"
      >
        Ångra
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={!canRedo}
        onClick={onRedo}
        title="Gör om (Ctrl+Y)"
      >
        Gör om
      </Button>
      <div className="mx-1 h-5 w-px bg-[var(--color-border)]" aria-hidden="true" />
      <Button
        variant="ghost"
        size="sm"
        onClick={onExportPdf}
        title="Exportera som PDF (Ctrl+P)"
      >
        Exportera PDF
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onExportBom}
        title="Exportera apparatlista som CSV"
      >
        Apparatlista
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onExportConductors}
        title="Exportera trådlista som CSV"
      >
        Trådlista
      </Button>
      <div className="mx-1 h-5 w-px bg-[var(--color-border)]" aria-hidden="true" />
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpenMeta}
        title="Ritningsinställningar (projekt, beställare, konstruktör)"
      >
        Inställningar
      </Button>
      <div className="ml-auto flex items-center gap-2">
        <AutosaveBadge status={autosave} />
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenReleaseNotes}
          title="Visa releasenoter"
        >
          Nyheter
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenCommandPalette}
          title="Kommandon (Ctrl+K)"
        >
          Kommandon
          <kbd className="ml-2 rounded border border-[var(--color-border)] bg-white px-1.5 text-[10px] font-mono text-[var(--color-muted)]">
            Ctrl+K
          </kbd>
        </Button>
      </div>
    </div>
  );
}

function AutosaveBadge({ status }: { status: AutosaveStatus }) {
  if (status.state === "idle") return null;
  if (status.state === "saving") {
    return (
      <span
        className="text-xs text-[var(--color-muted)]"
        aria-live="polite"
        title="Sparar automatiskt till din browsers lokala lagring"
      >
        Sparar…
      </span>
    );
  }
  const tsLabel = status.lastSavedAt ? formatClock(status.lastSavedAt) : "";
  return (
    <span
      className="text-xs text-[var(--color-muted)]"
      aria-live="polite"
      title="Automatiskt sparat lokalt — stäng fliken, öppna igen, allt finns kvar"
    >
      Sparad {tsLabel}
    </span>
  );
}

function formatClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}
